import { PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Card, Drawer, List, message } from "antd";
import React, { useEffect, useState } from "react";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { DayTemplateModal } from "./dayTemplateModal.jsx";
import { OrderDeleteButtons } from "~/pages/admin/dto/components/OrderDeleteButtons.jsx";
import { HelpLine } from "~/components/help/helpLine.jsx";

export const DayTemplatesDrawer = ({ club }) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplatesState] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const setTemplates = (data) => {
    setTemplatesState(
      data.map((d) => ({
        ...d,
        registration_lists: d?.registration_lists?.map((list, index) => ({
          ...list,
          id: `new_${index}`,
        })),
        diensten: d?.diensten?.map((dienst, index) => ({
          ...dienst,
          id: `new_${index}`,
        })),
      }))
    );
  };

  const fetchTemplates = async () => {
    setLoading(true);
    const data = await api.get("days/day_templates.json");
    if (data) {
      setTemplates(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (showDrawer) {
      fetchTemplates();
    }
  }, [showDrawer]);

  const openDrawer = () => {
    setShowDrawer(true);
  };

  const closeDrawer = () => {
    setShowDrawer(false);
  };

  const handleAddNew = () => {
    setActiveTemplate(null);
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setActiveTemplate(template);
    setShowModal(true);
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setActiveTemplate(null);
  };

  const handleModalOk = async (values) => {
    try {
      setConfirmLoading(true);
      let data = null;

      // filter out diensten and registration lists marked for deletion as for template we don't need this
      values.diensten = values.diensten.filter((d) => !d.delete);
      values.registration_lists = values.registration_lists.filter(
        (l) => !l.delete
      );

      if (activeTemplate?.id) {
        // PATCH existing template
        data = await api.patch("days/day_templates.json", {
          ...values,
          id: activeTemplate.id,
        });
        message.success($t("Succesvol bijgewerkt"));
        data = data.data;
      } else {
        // POST new template
        data = await api.post2("days/day_templates.json", values);
        message.success($t("Succesvol aangemaakt"));
        data = data.data;
      }

      setShowModal(false);
      setActiveTemplate(null);

      if (data) setTemplates(data);
    } catch (error) {
      console.error("Error saving template:", error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    try {
      setConfirmLoading(true);
      const { data } = await api.delete("days/day_templates.json", {
        id: templateId,
      });
      message.success($t("Succesvol verwijderd"));
      setShowModal(false);
      setActiveTemplate(null);
      if (data) setTemplates(data);
    } catch (error) {
      console.error("Error deleting template:", error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleTemplateOrderChange = async (updatedTemplates) => {
    try {
      setLoading(true);

      const { data } = await api.put("days/day_templates.json", {
        templates: updatedTemplates,
      });

      if (data) setTemplates(data);
    } catch (error) {
      console.error("Error saving template order:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={openDrawer}
        icon={<SettingOutlined />}
        size="small"
        type="default"
      >
        {$t("Dag templates")}
      </Button>

      <Drawer
        title={$t("Dag templates beheren")}
        placement="right"
        width={600}
        open={showDrawer}
        onClose={closeDrawer}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
            size="small"
          >
            {$t("Toevoegen")}
          </Button>
        }
      >
        <HelpLine>
          {$t(
            "Dag templates leveren bij ad hoc aangemaakte dagen, of tijdens het maken van roosters, voorafingestelde diensten en/of registratielijsten."
          )}
        </HelpLine>

        <List
          style={{ marginTop: 12 }}
          loading={loading}
          dataSource={templates}
          locale={{
            emptyText: $t("Geen dag templates gevonden"),
          }}
          renderItem={(template, index) => (
            <List.Item>
              <Card
                size="small"
                style={{ width: "100%" }}
                title={template.name}
                extra={
                  <div className="row" style={{ gap: 6 }}>
                    <OrderDeleteButtons
                      key="order"
                      loading={loading}
                      task_index={index}
                      task={template}
                      tasks={templates}
                      saveTasks={handleTemplateOrderChange}
                      hasDelete={false}
                    />

                    <Button size="small" onClick={() => handleEdit(template)}>
                      {$t("Edit")}
                    </Button>
                  </div>
                }
              >
                {template.description && <p>{template.description}</p>}
              </Card>
            </List.Item>
          )}
        />
      </Drawer>

      <DayTemplateModal
        club={club}
        visible={showModal}
        onCancel={handleModalCancel}
        onOk={handleModalOk}
        onDelete={handleDelete}
        confirmLoading={confirmLoading}
        activeTemplate={activeTemplate}
        templateCount={templates.length}
      />
    </>
  );
};
